// ==============================================================================
// CLIENTE HTTP DESACOPLADO DA API DE DIAGNÓSTICO (FRONTEND)
// ==============================================================================
// Regra de ouro de arquitetura:
// O frontend NÃO importa nem conhece handlers de servidor, service_role,
// conexões diretas ao Postgres ou rate limiters persistidos.
// Em produção, dispara chamadas HTTP via fetch para os endpoints /functions/v1/*.
// Em desenvolvimento local / testes, utiliza um adapter mock explícito de interface.
// ==============================================================================

import type { CategoryKey, LeadIdentification } from '@/domains/assessment/types';
import type { PublicDiagnosticResult, OverallTierKey } from '@/domains/results/types';
import type { UtmParameters } from '@/domains/analytics/types';

export interface SubmitDiagnosticPayload {
  answers: {
    categoryKey: CategoryKey;
    questionIndex: number;
    scaleValue: 0 | 1 | 2 | 3;
  }[];
  lead: LeadIdentification;
  consents: {
    consentOperational: boolean;
    consentCommercial: boolean;
    consentVersion?: string;
    consentOrigin?: string;
  };
  utms?: UtmParameters;
  turnstileToken?: string;
  honeypot?: string;
}

export interface SubmitDiagnosticResponse {
  success: boolean;
  publicToken: string;
  leadFirstName: string;
  overallScore: number;
  overallTier: {
    key: string;
    label: string;
  };
  categoryScores: Record<CategoryKey, number>;
  primaryPriority: {
    key: CategoryKey;
    name: string;
    percentage: number;
  };
  secondaryPriority?: {
    key: CategoryKey;
    name: string;
    percentage: number;
  };
  hasRetentionRisk: boolean;
  whatsappUrl: string;
  completedAt: string;
}

export interface DiagnosticApiClient {
  submitDiagnostic: (payload: SubmitDiagnosticPayload) => Promise<PublicDiagnosticResult>;
  getPublicResult: (token: string) => Promise<PublicDiagnosticResult>;
}

// ------------------------------------------------------------------------------
// Adapter Mock Explícito para Desenvolvimento Local e Testes de Interface
// ------------------------------------------------------------------------------
class MockDiagnosticApiAdapter implements DiagnosticApiClient {
  async submitDiagnostic(payload: SubmitDiagnosticPayload): Promise<PublicDiagnosticResult> {
    // Simula delay de rede seguro
    await new Promise((resolve) => setTimeout(resolve, 600));

    // Validação básica do contrato de interface
    if (payload.answers.length !== 18) {
      throw new Error('Devem ser fornecidas exatamente 18 respostas.');
    }
    if (!payload.consents.consentOperational) {
      throw new Error('O consentimento operacional é obrigatório.');
    }

    const leadFirstName = payload.lead.name.trim().split(' ')[0] || 'Colega';

    // Agrupa e calcula as notas para exibição consistente na interface
    const categorySums: Record<CategoryKey, number> = {
      positioning: 0,
      founder_authority: 0,
      digital_assets: 0,
      distribution: 0,
      commercial_conversation: 0,
      customer_experience: 0,
    };

    payload.answers.forEach((ans) => {
      categorySums[ans.categoryKey] += ans.scaleValue;
    });

    const categoryScores: Record<CategoryKey, number> = {
      positioning: Math.round((categorySums.positioning / 9) * 100),
      founder_authority: Math.round((categorySums.founder_authority / 9) * 100),
      digital_assets: Math.round((categorySums.digital_assets / 9) * 100),
      distribution: Math.round((categorySums.distribution / 9) * 100),
      commercial_conversation: Math.round((categorySums.commercial_conversation / 9) * 100),
      customer_experience: Math.round((categorySums.customer_experience / 9) * 100),
    };

    const total = Object.values(categoryScores).reduce((acc, v) => acc + v, 0);
    const overallScore = Math.round(total / 6);

    let tierKey: OverallTierKey = 'vulnerable' as OverallTierKey;
    let tierLabel = 'Presença vulnerável';
    if (overallScore >= 80) {
      tierKey = 'structured' as OverallTierKey;
      tierLabel = 'Presença acelerada';
    } else if (overallScore >= 60) {
      tierKey = 'structured';
      tierLabel = 'Presença estruturada';
    } else if (overallScore >= 40) {
      tierKey = 'building' as OverallTierKey;
      tierLabel = 'Presença em construção';
    }

    // Identifica menor nota
    const sorted = (Object.keys(categoryScores) as CategoryKey[]).sort(
      (a, b) => categoryScores[a] - categoryScores[b],
    );
    const primaryKey = sorted[0] || 'positioning';
    const secondaryKey = sorted[1];

    const categoryNames: Record<CategoryKey, string> = {
      positioning: 'Posicionamento',
      founder_authority: 'Autoridade do fundador',
      digital_assets: 'Perfil e site',
      distribution: 'Distribuição',
      commercial_conversation: 'Conversa comercial',
      customer_experience: 'Experiência do cliente',
    };

    const publicToken = crypto.randomUUID ? crypto.randomUUID() : '123e4567-e89b-12d3-a456-426614174000';
    const message = encodeURIComponent(
      `Olá Israel! Acabei de fazer o Diagnóstico de Presença Estratégica da minha contabilidade. Tirei nota ${overallScore}% (${tierLabel}) e minha prioridade principal foi "${categoryNames[primaryKey]}". Quero conversar sobre como dar o próximo passo!`,
    );

    return {
      publicToken,
      leadFirstName,
      overallScore,
      overallTier: {
        min: 0,
        max: 100,
        key: tierKey,
        label: tierLabel,
      },
      categoryScores,
      primaryPriority: {
        key: primaryKey,
        name: categoryNames[primaryKey],
      },
      secondaryPriority: secondaryKey && categoryScores[secondaryKey] < 70 ? {
        key: secondaryKey,
        name: categoryNames[secondaryKey],
      } : undefined,
      hasRetentionRisk: categoryScores.customer_experience < 40,
      algorithmVersion: 'v1.0.0',
      completedAt: new Date().toISOString(),
      whatsappUrl: `https://wa.me/${import.meta.env.VITE_WHATSAPP_NUMBER || '5581992451821'}?text=${message}`,
    };
  }

  async getPublicResult(token: string): Promise<PublicDiagnosticResult> {
    await new Promise((resolve) => setTimeout(resolve, 300));

    if (!token || token.length < 10) {
      throw new Error('Token de diagnóstico inválido.');
    }

    // Retorna resultado consistente para o token informado
    const message = encodeURIComponent(
      'Olá Israel! Acabei de consultar o Diagnóstico de Presença Estratégica da minha contabilidade. Quero conversar sobre como dar o próximo passo!',
    );

    return {
      publicToken: token,
      leadFirstName: 'Gestor',
      overallScore: 48,
      overallTier: {
        min: 40,
        max: 59,
        key: 'building',
        label: 'Presença em construção',
      },
      categoryScores: {
        positioning: 33,
        founder_authority: 55,
        digital_assets: 44,
        distribution: 22,
        commercial_conversation: 67,
        customer_experience: 67,
      },
      primaryPriority: {
        key: 'distribution',
        name: 'Distribuição',
      },
      secondaryPriority: {
        key: 'positioning',
        name: 'Posicionamento',
      },
      hasRetentionRisk: false,
      algorithmVersion: 'v1.0.0',
      completedAt: new Date().toISOString(),
      whatsappUrl: `https://wa.me/${import.meta.env.VITE_WHATSAPP_NUMBER || '5581992451821'}?text=${message}`,
    };
  }
}

// ------------------------------------------------------------------------------
// Cliente HTTP Real para Ambiente de Produção
// ------------------------------------------------------------------------------
class HttpDiagnosticApiClient implements DiagnosticApiClient {
  private baseUrl: string;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl || (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SUPABASE_URL) || '';
  }

  async submitDiagnostic(payload: SubmitDiagnosticPayload): Promise<PublicDiagnosticResult> {
    const url = `${this.baseUrl}/functions/v1/submit-diagnostic`;
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      if (res.status === 429) {
        throw new Error('Limite de tentativas excedido. Por favor, aguarde alguns minutos antes de tentar novamente.');
      }
      const errorJson = await res.json().catch(() => ({}));
      throw new Error(errorJson.error || `Erro ao processar diagnóstico (HTTP ${res.status}).`);
    }

    const data = await res.json();
    return {
      publicToken: data.publicToken,
      leadFirstName: data.leadFirstName,
      overallScore: data.overallScore,
      overallTier: {
        min: 0,
        max: 100,
        key: data.overallTier.key,
        label: data.overallTier.label,
      },
      categoryScores: data.categoryScores,
      primaryPriority: data.primaryPriority,
      secondaryPriority: data.secondaryPriority,
      hasRetentionRisk: data.hasRetentionRisk,
      algorithmVersion: 'v1.0.0',
      completedAt: data.completedAt,
      whatsappUrl: data.whatsappUrl,
    };
  }

  async getPublicResult(token: string): Promise<PublicDiagnosticResult> {
    const url = `${this.baseUrl}/functions/v1/get-public-result?token=${encodeURIComponent(token)}`;
    const res = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!res.ok) {
      if (res.status === 404) {
        throw new Error('Diagnóstico não encontrado para o token fornecido.');
      }
      throw new Error(`Erro ao carregar diagnóstico (HTTP ${res.status}).`);
    }

    const data = await res.json();
    return {
      publicToken: data.publicToken,
      leadFirstName: data.leadFirstName,
      overallScore: data.overallScore,
      overallTier: {
        min: 0,
        max: 100,
        key: data.overallTier.key,
        label: data.overallTier.label,
      },
      categoryScores: data.categoryScores,
      primaryPriority: data.primaryPriority,
      secondaryPriority: data.secondaryPriority,
      hasRetentionRisk: data.hasRetentionRisk,
      algorithmVersion: data.algorithmVersion || 'v1.0.0',
      completedAt: data.completedAt,
      whatsappUrl: data.whatsappUrl,
    };
  }
}

// ------------------------------------------------------------------------------
// Fábrica e Exportação Principal
// ------------------------------------------------------------------------------
const isProduction =
  typeof import.meta !== 'undefined' &&
  import.meta.env?.VITE_APP_ENV === 'production' &&
  Boolean(import.meta.env?.VITE_SUPABASE_URL);

export const diagnosticApi: DiagnosticApiClient = isProduction
  ? new HttpDiagnosticApiClient()
  : new MockDiagnosticApiAdapter();

export function createDiagnosticApi(options?: { useMock?: boolean; baseUrl?: string }): DiagnosticApiClient {
  if (options?.useMock ?? true) {
    return new MockDiagnosticApiAdapter();
  }
  return new HttpDiagnosticApiClient(options?.baseUrl);
}
